import type { ImpedanceDataDto } from '../../types';

interface SavedData {
  id: string;
  name: string;
  data: ImpedanceDataDto;
  timestamp?: Date;
}

interface SaveLoadControlsProps {
  savedItems: SavedData[];
  currentName: string;
  onNameChange: (name: string) => void;
  onSave: () => void;
  onLoad: (id: string) => void;
  onDelete: (id: string) => void;
  title: string;
  isSaving?: boolean;
}

export function SaveLoadControls({
  savedItems,
  currentName,
  onNameChange,
  onSave,
  onLoad,
  onDelete,
  title,
  isSaving
}: SaveLoadControlsProps) {
  return (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4">
      {/* Save Section */}
      <div>
        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Save {title}</h5>
        <div className="flex gap-2">
          <input
            type="text"
            value={currentName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={`Enter ${title.toLowerCase()} name...`}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
          />
          <button
            onClick={onSave}
            disabled={isSaving || !currentName.trim()}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded text-sm font-medium transition-colors"
          >
            {isSaving ? '...' : '💾 Save'}
          </button>
        </div>
      </div>

      {/* Load Section */}
      {savedItems.length > 0 && (
        <div>
          <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            Saved {title}s ({savedItems.length})
          </h5>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {savedItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{item.name}</p>
                  {item.timestamp && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {item.data.frequency_Hz.length} points
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onLoad(item.id)}
                    className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors"
                  >
                    Load
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete "${item.name}"? This cannot be undone.`)) {
                        onDelete(item.id);
                      }
                    }}
                    className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
